export const tier1Languages = [
  'en',
  'de',
  'es',
  'fr',
  'it',
  'ja',
  'ko',
  'pt-BR',
  'ru',
  'zh-CN',
  'zh-TW'
] as const
export const tier2Languages = [
  'ar',
  'bg',
  'ca',
  'cs',
  'da',
  'el',
  'fi',
  'he',
  'hr',
  'hu',
  'id',
  'ms',
  'nb',
  'nl',
  'pl',
  'sk',
  'sv',
  'th',
  'tr',
  'uk'
] as const
export const supportedLanguages = [...tier1Languages, ...tier2Languages] as const

export type SupportedLanguage = (typeof supportedLanguages)[number]

export const isSupportedLanguage = (language: string): language is SupportedLanguage => {
  return supportedLanguages.includes(language as SupportedLanguage)
}

type ApplicationLanguage = {code: SupportedLanguage; label: string}

export const tier1ApplicationLanguages: ApplicationLanguage[] = [
  {code: 'en', label: 'English'},
  {code: 'de', label: 'Deutsch'},
  {code: 'es', label: 'Español'},
  {code: 'fr', label: 'Français'},
  {code: 'it', label: 'Italiano'},
  {code: 'ja', label: '日本語'},
  {code: 'ko', label: '한국어'},
  {code: 'pt-BR', label: 'Português (BR)'},
  {code: 'ru', label: 'Русский'},
  {code: 'zh-CN', label: '简体中文'},
  {code: 'zh-TW', label: '繁體中文'}
]
export const tier2ApplicationLanguages: ApplicationLanguage[] = [
  {code: 'ar', label: 'العربية'},
  {code: 'bg', label: 'Български'},
  {code: 'ca', label: 'Català'},
  {code: 'cs', label: 'Čeština'},
  {code: 'da', label: 'Dansk'},
  {code: 'el', label: 'Ελληνικά'},
  {code: 'fi', label: 'Suomi'},
  {code: 'he', label: 'עברית'},
  {code: 'hr', label: 'Hrvatski'},
  {code: 'hu', label: 'Magyar'},
  {code: 'id', label: 'Bahasa Indonesia'},
  {code: 'ms', label: 'Bahasa Melayu'},
  {code: 'nb', label: 'Bokmål'},
  {code: 'nl', label: 'Nederlands'},
  {code: 'pl', label: 'Polski'},
  {code: 'sk', label: 'Slovenčina'},
  {code: 'sv', label: 'Svenska'},
  {code: 'th', label: 'ไทย'},
  {code: 'tr', label: 'Türkçe'},
  {code: 'uk', label: 'Українська'}
]
export const applicationLanguages: ApplicationLanguage[] = [
  ...tier1ApplicationLanguages,
  ...tier2ApplicationLanguages
]

type LanguageVariant = keyof typeof languagesVariants

export const languagesVariants = {
  'zh-CN': 'Chinese, Simplified',
  'zh-HK': 'Chinese, Traditional (Hong Kong)',
  'zh-MO': 'Chinese, Traditional (Macau)',
  'zh-TW': 'Chinese, Traditional (Taiwan)',

  'en-US': 'English (USA)',
  'en-GB': 'English (Great Britain)',
  'en-AU': 'English (Australia)',
  'en-CA': 'English (Canada)',
  'en-IN': 'English (India)',
  'en-IE': 'English (Ireland)',
  'en-NZ': 'English (New Zealand)',
  'en-SG': 'English (Singapore)',
  'en-ZA': 'English (South Africa)',

  'fr-FR': 'French (France)',
  'fr-BE': 'French (Belgium)',
  'fr-CA': 'French (Canada)',
  'fr-CH': 'French (Swiss)',

  'de-DE': 'German (Germany)',

  'it-IT': 'Italian (Italy)',
  'it-CH': 'Italian (Swiss)',

  'ja-JP': 'Japanese (Japan)',

  'ko-KR': 'Korean (Korea)',

  'pt-BR': 'Portuguese (Brazil)',
  'pt-PT': 'Portuguese (Portugal)',

  'ru-RU': 'Russian (Russia)',

  'es-ES': 'Spanish (Spain)',
  'es-419': 'Spanish (Latin America)',
  'es-AR': 'Spanish (Argentina)',
  'es-CO': 'Spanish (Colombia)',
  'es-MX': 'Spanish (Mexico)',
  'es-PE': 'Spanish (Peru)',
  'es-PR': 'Spanish (Puerto Rico)',
  'es-US': 'Spanish (USA)',
  'es-VE': 'Spanish (Venezuela)',

  'ar-SA': 'Arabic (Saudi Arabia)',
  'bg-BG': 'Bulgarian (Bulgaria)',
  'ca-ES': 'Catalan (Spain)',
  'cs-CZ': 'Czech (Czech Republic)',
  'da-DK': 'Danish (Denmark)',
  'el-GR': 'Greek (Greece)',
  'fi-FI': 'Finnish (Finland)',
  'he-IL': 'Hebrew (Israel)',
  'hr-HR': 'Croatian (Croatia)',
  'hu-HU': 'Hungarian (Hungary)',
  'id-ID': 'Indonesian (Indonesia)',
  'ms-MY': 'Malaysian (Malaysia)',
  'nb-NO': 'Norwegian Bokmål (Norway)',
  'nl-NL': 'Dutch (Netherlands)',
  'pl-PL': 'Polish (Poland)',
  'sk-SK': 'Slovak (Slovakia)',
  'sv-SE': 'Swedish (Sweden)',
  'th-TH': 'Thai (Thailand)',
  'tr-TR': 'Turkish (Turkey)',
  'uk-UA': 'Ukrainian (Ukraine)'
} as const

export const defaultVariantMap: Record<SupportedLanguage, LanguageVariant> = {
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  ja: 'ja-JP',
  ko: 'ko-KR',
  ['pt-BR']: 'pt-BR',
  ru: 'ru-RU',
  de: 'de-DE',
  ['zh-CN']: 'zh-CN',
  ['zh-TW']: 'zh-TW',
  ar: 'ar-SA',
  bg: 'bg-BG',
  ca: 'ca-ES',
  cs: 'cs-CZ',
  da: 'da-DK',
  el: 'el-GR',
  fi: 'fi-FI',
  he: 'he-IL',
  hr: 'hr-HR',
  hu: 'hu-HU',
  id: 'id-ID',
  ms: 'ms-MY',
  nb: 'nb-NO',
  nl: 'nl-NL',
  pl: 'pl-PL',
  sk: 'sk-SK',
  sv: 'sv-SE',
  th: 'th-TH',
  tr: 'tr-TR',
  uk: 'uk-UA'
} as const

export type LangPair = Record<'source' | 'target', SupportedLanguage>

export type LocalizedImageName = `${string}.${'png' | 'webp'}`

export const getLocalizedImage = (locale: string, imageName: LocalizedImageName) =>
  `/images/localized/${locale}/${imageName}`

export const detectBrowserLanguage = () => {
  const browserLanguages = navigator.languages

  const supportedLanguagesSet = new Set(supportedLanguages)
  const variantValues = new Set(Object.values(defaultVariantMap))

  for (const browserLang of browserLanguages) {
    // Attempt 1: exact match
    if (supportedLanguagesSet.has(browserLang as SupportedLanguage)) {
      return browserLang as SupportedLanguage
    }

    // Attempt 2: variant match
    if (variantValues.has(browserLang as keyof typeof languagesVariants)) {
      const variantMatchKey = Object.entries(defaultVariantMap).find(
        ([, value]) => value === browserLang
      )?.[0]
      if (variantMatchKey) return variantMatchKey as SupportedLanguage
    }

    // Attempt 3: root language match
    const rootLang = browserLang.split('-')[0]
    const rootMatch = supportedLanguages.find(supported => supported.startsWith(rootLang))
    if (rootMatch) return rootMatch
  }

  return null
}

// NOTE: Add back if needed

// export type LangPairString = `${SupportedLanguage}-${SupportedLanguage}`

// export const generateLanguagePairs = () => {
//   const pairs: LangPairString[] = []

//   for (const source of supportedLanguages) {
//     for (const target of supportedLanguages) {
//       if (source !== target) {
//         pairs.push(`${source}-${target}`)
//       }
//     }
//   }

//   return pairs
// }

// export const parseLangPair = (input: string) => {
//   if (!input) return null

//   const cleanSection = input.split('?')[0]
//   const parts = cleanSection.split('-')
//   let pair: {source: string; target: string} | null = null

//   if (parts.length === 2) pair = {source: parts[0], target: parts[1]}

//   if (parts.length === 3) {
//     const possibleTarget = `${parts[1]}-${parts[2]}`
//     const possibleSource = `${parts[0]}-${parts[1]}`

//     if (isSupportedLanguage(possibleTarget)) pair = {source: parts[0], target: possibleTarget}
//     if (isSupportedLanguage(possibleSource)) pair = {source: possibleSource, target: parts[2]}
//   }

//   if (parts.length === 4) {
//     pair = {
//       source: `${parts[0]}-${parts[1]}`,
//       target: `${parts[2]}-${parts[3]}`
//     }
//   }

//   if (pair && (!isSupportedLanguage(pair.source) || !isSupportedLanguage(pair.target))) {
//     return null
//   }

//   return pair as LangPair | null
// }

// export const makeLangPairPath = (langPair: LangPair | null) => {
//   return langPair ? `/${langPair.source}-${langPair.target}` : ''
// }
